import React, { useMemo } from 'react';
import { List, Spin, Empty, Badge } from 'antd';
import { EnvironmentOutlined, LoadingOutlined } from '@ant-design/icons';
import { trLower } from '../../utils/trText';

const ResultsList = ({ data, onSelect, loading, darkMode, selectedItemId, isBkmMode = false, isMobile = false }) => {
  const segmentLabels = {
    0: '?',
    1: 'E',
    2: 'D',
    3: 'C',
    4: 'B',
    5: 'A',
    6: 'A+'
  };

  const lokasyonFirmalar = {
    0: "ATM POINT A.Ş.",
    1: "ATM City A.Ş.",
    2: "ATM İSTANBUL A.Ş.", 
    9: "ATM KİRALAMA A.Ş.",
    99: "Offsite",
  }

  const renderSegment = (segment) => (
    segmentLabels[segment] || '?'
  );

  const getStatusColor = (item) => {
    if (selectedItemId === item.id) return '#1890ff';

    const statusText = trLower(item.status_text || item.durum_name || '');

    if (statusText.includes('aday') || (statusText.includes('pasif') && statusText.includes('aday'))) {
      return '#8c8c8c';
    }
    else if (statusText.includes('pasif')) {
      return '#f5222d';
    }
    else if (statusText.includes('aktif')) {
      return '#52c41a';
    }

    return '#d9d9d9';
  };

  const getBorderColor = (item) => {
    return selectedItemId === item.id ? '#1890ff' : getStatusColor(item);
  };

  const orderedData = useMemo(() => {
    if (!selectedItemId || !Array.isArray(data) || data.length === 0) return data;
    const idx = data.findIndex((it) => String(it.id) === String(selectedItemId));
    if (idx <= 0) return data;
    const arr = data.slice();
    return arr;
  }, [data, selectedItemId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  if (data.length === 0) {
    return <Empty description="Sonuç bulunamadı" />;
  }

  return (
    <List
      itemLayout="horizontal"
      dataSource={orderedData}
      renderItem={(item, index) => {
        const isSelected = selectedItemId === item.id;
        const borderColor = getBorderColor(item);
        const isBkmItem = isBkmMode || item.bkm;
        return (
          <List.Item
            key={item.id}
            className="result-item"
            onClick={() => onSelect(item)}
            style={{
              cursor: 'pointer',
              padding: isMobile ? '6px' : '8px',
              borderRadius: '4px',
              backgroundColor: isSelected ? (darkMode ? '#1a3a6e' : '#e6f7ff') : 'inherit',
              marginBottom: isMobile ? '6px' : '8px',
              transition: 'all 0.3s',
              border: `${isMobile ? '1px' : '2px'} solid ${borderColor}`,
              boxShadow: isSelected ? '0 0 0 2px rgba(24, 144, 255, 0.2)' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = darkMode ? '#383838' : '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = darkMode ? 'var(--dark-card-bg)' : '#fff';
            }}
          >
            <List.Item.Meta
              avatar={
                <Badge
                  count={index + 1}
                  style={{
                    backgroundColor: getStatusColor(item),
                    fontSize: isMobile ? '10px' : '12px',
                    minWidth: isMobile ? '18px' : '20px',
                    height: isMobile ? '18px' : '20px',
                    lineHeight: isMobile ? '18px' : '20px'
                  }}
                >
                  <EnvironmentOutlined
                    style={{
                      fontSize: isMobile ? 20 : 24,
                      color: getStatusColor(item)
                    }}
                  />
                </Badge>
              }
              title={
                isBkmItem ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: darkMode ? '#fff' : '#333', fontSize: isMobile ? '13px' : '14px' }}>
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: isMobile ? 'nowrap' : 'normal',
                      maxWidth: isMobile ? '150px' : 'none' 
                    }}>
                      {item.banka || item.name || 'BKM ATM'}
                    </span>
                    {item.onsite && (
                      <span style={{
                        fontSize: isMobile ? '0.7em' : '0.75em',
                        padding: isMobile ? '1px 4px' : '2px 6px',
                        borderRadius: 12,
                        background: darkMode ? '#2a2a2a' : '#fafafa',
                        border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                        color: darkMode ? '#ddd' : '#666'
                      }}>
                        {item.onsite}
                      </span>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: darkMode ? '#fff' : '#333', fontSize: isMobile ? '13px' : '14px' }}>
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: isMobile ? 'nowrap' : 'normal',
                      flex: 1,
                      marginRight: '8px'
                    }}>
                      {item.name}
                    </span>
                    <span style={{ fontSize: isMobile ? '0.75em' : '0.8em', color: darkMode ? '#ccc' : '#888', flexShrink: 0 }}>
                      {item.maptriksID}
                    </span>
                  </div>
                )
              }
              description={
                isBkmItem ? (
                  <div style={{ color: darkMode ? '#ccc' : '#666', fontSize: isMobile ? '11px' : '12px' }}>
                    <div><strong>Adres:</strong> {item.address || '-'}</div>
                  </div>
                ) : (
                  <div style={{ color: darkMode ? '#ccc' : '#666', fontSize: isMobile ? '11px' : '12px' }}>
                    <div style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: isMobile ? '-webkit-box' : 'block',
                      WebkitLineClamp: isMobile ? 2 : 'none',
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {item.address}
                    </div>
                    <div style={{ marginTop: isMobile ? '6px' : '8px', display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? '8px' : '4px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                      <div>
                        <strong>Firma:</strong> {isMobile ? (lokasyonFirmalar?.[item.firma]?.substring(0, 10) || 'N/A') : (item.firma_adi || lokasyonFirmalar?.[item.firma] || 'Bilinmiyor')}
                      </div>
                      {!isMobile && (
                        <div>
                          <strong>Kategori:</strong> {item.kategori_name || item.kategori || 'Belirtilmemiş'}
                        </div>
                      )}
                    </div>
                  </div>
                )
              }
            />
          </List.Item>
        );
      }}
    />
  );
};

export default ResultsList;